import React, { useEffect, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IMainState } from 'store/states'
import { IFoldersState } from 'store/states/folders.state'
import * as fromActions from 'store/actions/folders.action'
import Loader from 'components/ui/loader/Loader'
import Folder from './folder/Folder'
import New from './new/New'
import { Alert } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faGripLines } from '@fortawesome/pro-solid-svg-icons'
import styles from './List.module.scss'

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd'
import { IFolderModel } from 'models/folder.model'
import { useTranslation } from 'react-i18next'

interface IProps {
  showCreate: boolean
  orderFolders: boolean
  setShowCreate: any
  setData: any
  data: IFolderModel[]
}

const List: React.FC<IProps> = ({
  showCreate,
  setShowCreate,
  orderFolders,
  setData,
  data,
}) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const { list, loading } = useSelector(
    ({ folders }: IMainState) => folders as IFoldersState
  )

  useEffect(() => {
    dispatch({ type: fromActions.FOLDERS_ADMINISTRATION_LIST })
  }, [dispatch])

  const reorder = (
    list: IFolderModel[],
    startIndex: number,
    endIndex: number
  ) => {
    const target = list[startIndex]
    const folder = list[endIndex]
    dispatch({
      type: fromActions.ORDER_FOLDER,
      payload: { idFolder: folder.id, idTarget: target.id },
    })

    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const items = reorder(list, result.source.index, result.destination.index)

    setData(items)
  }

  return (
    <Fragment>
      {loading && <Loader overlay color />}
      {list.length ? (
        <Fragment>
          {showCreate && (
            <New
              setShowCreate={setShowCreate}
              lastItemOrder={list[0].order + 1}
            />
          )}
          {orderFolders ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {data.map((folder, index) => (
                      <Draggable
                        key={folder.id}
                        draggableId={index.toString()}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="d-flex align-items-center p-3 border-bottom justify-content-between"
                          >
                            <div className="d-flex align-items-center">
                              <FontAwesomeIcon
                                size="1x"
                                color="#808080"
                                icon={faGripLines}
                                className="mr-2"
                              />
                              <div className="d-flex flex-column">
                                <span>{folder.name}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            list.map((folder) => (
              <Folder key={folder.id} item={folder} create={showCreate} />
            ))
          )}
        </Fragment>
      ) : (
        !loading && (
          <Alert variant="primary" className={styles.notLoaded}>
            <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>
            <span className={styles.notLoadedText}>
              {t('administration.folders.description')}
            </span>
          </Alert>
        )
      )}
    </Fragment>
  )
}

export default List
